const SENTRY_TRACE_PATTERN = /Sentry Trace:\s*([a-z0-9-]+)/gi;

function shouldSkipElement(element: Element | null): boolean {
  if (!element) {
    return true;
  }

  return Boolean(
    element.closest('a,button,input,textarea,select,script,style,[contenteditable="true"]')
  );
}

function linkifyTextNode(textNode: Text): void {
  const container = textNode.parentElement;

  if (shouldSkipElement(container)) {
    return;
  }

  const text = textNode.nodeValue ?? '';
  SENTRY_TRACE_PATTERN.lastIndex = 0;

  if (!SENTRY_TRACE_PATTERN.test(text)) {
    return;
  }

  const fragment = document.createDocumentFragment();
  let lastIndex = 0;

  SENTRY_TRACE_PATTERN.lastIndex = 0;
  let match = SENTRY_TRACE_PATTERN.exec(text);

  while (match) {
    const [fullMatch, traceId] = match;
    const start = match.index;

    if (start > lastIndex) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
    }

    const anchor = document.createElement('a');
    anchor.href = `/tools/trace-viewer?traceId=${encodeURIComponent(traceId)}`;
    anchor.target = '_blank';
    anchor.textContent = fullMatch;
    anchor.className =
      'text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800';
    fragment.appendChild(anchor);

    lastIndex = start + fullMatch.length;
    match = SENTRY_TRACE_PATTERN.exec(text);
  }

  if (lastIndex < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
  }

  textNode.replaceWith(fragment);
}

function linkifySentryTraces(root: Node): void {
  if (root.nodeType === Node.TEXT_NODE) {
    linkifyTextNode(root as Text);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = root as Element;

  if (shouldSkipElement(element)) {
    return;
  }

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  textNodes.forEach(linkifyTextNode);
}

export function startSentryTraceLinkifier(root: HTMLElement = document.body): () => void {
  let isApplying = false;

  const applyLinkifier = (node: Node): void => {
    if (isApplying) {
      return;
    }

    isApplying = true;

    try {
      linkifySentryTraces(node);
    } finally {
      isApplying = false;
    }
  };

  applyLinkifier(root);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'characterData') {
        applyLinkifier(mutation.target);
        return;
      }

      mutation.addedNodes.forEach((node) => {
        applyLinkifier(node);
      });
    });
  });

  observer.observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return () => {
    observer.disconnect();
  };
}
